<template>
  <div class="marketplace" :class="{ 'is-editable': editable }">
    <template v-if="viewmode === GRID">
      <el-row v-infinite-scroll="showMore" style="overflow: auto;" :gutter="12">
        <draggable
          v-model="visiblePartnerships"
          :class="{ 'draggable-enabled': draggable }"
          v-bind="dragOptions"
        >
          <el-col
            v-for="(partnership, index) in visiblePartnerships"
            :key="partnership.id"
            :lg="6"
            :md="8"
            :sm="12"
            :xl="4"
            :xs="24"
          >
            <el-card
              :class="{ 'is-draft': isDraft(partnership) }"
              class="partnership-item"
              shadow="hover"
            >
              <div
                v-if="isDraft(partnership)"
                class="corner-ribbon top-right grey shadow"
              >
                Draft
              </div>
              <el-tooltip v-if="editable" content="Edit" placement="top">
                <ion-icon
                  class="edit-button"
                  name="create"
                  @click="onEdit({ partnership })"
                />
              </el-tooltip>
              <div class="image-wrapper">
                <img
                  :src="partnership.logo_url | cld({ h: 120, c: 'limit' })"
                  alt=""
                  class="card-img-top"
                />
              </div>
              <h1 class="title">
                {{ partnership.name }}
              </h1>
              <div class="tags">
                <el-tag
                  v-for="tag in partnership.tags"
                  :key="tag"
                  size="small"
                  type="info"
                >
                  {{ tag }}
                </el-tag>
              </div>

              <div
                class="description"
                v-html="
                  getUpdatedPartnershipDescription(partnership.description)
                "
              />
              <!-- <div class="description">{{ partnership.description }}</div> -->
              <div class="text-center">
                <el-button
                  class="action-button"
                  size="small"
                  @click="onPartnershipClick({ partnership })"
                >
                  Learn more
                </el-button>
              </div>
            </el-card>
          </el-col>
        </draggable>
      </el-row>
    </template>

    <template v-else-if="viewmode === TABLE">
      <el-table
        v-infinite-scroll="showMore"
        :data="visiblePartnerships"
        row-class-name="partnership-row"
        style="overflow: auto;"
      >
        <el-table-column label="Name">
          <template slot-scope="{ row }">
            <el-button
              type="text"
              size="small"
              @click="onPartnershipClick({ partnership: row })"
            >
              {{ row.name }}
            </el-button>
          </template>
        </el-table-column>
        <el-table-column label="Logo">
          <template slot-scope="{ row }">
            <img
              v-if="row.logo_url"
              class="partnership-logo"
              :src="row.logo_url | cld({ h: 60, c: 'limit' })"
            />
          </template>
        </el-table-column>
        <el-table-column label="Action">
          <template slot-scope="{ row }">
            <el-button
              class="action-button"
              size="small"
              @click="onPartnershipClick({ partnership: row })"
            >
              Learn more
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </template>

    <el-dialog
      :visible.sync="isPartnershipDetailsModalVisible"
      append-to-body
      :width="dialogWidth"
      @close="selectedPartnership = null"
      :close-on-click-modal="false"
    >
      <PartnershipDetails
        v-if="selectedPartnership"
        :partnership="selectedPartnership"
        @cancel="isPartnershipDetailsModalVisible = false"
        @done="
          isPartnershipDetailsModalVisible = false
          selectedPartnership = null
        "
      />
    </el-dialog>
  </div>
</template>

<script>
import { computed, ref, watch } from '@vue/composition-api'
import {
  DEFAULT_PROMO_LINK_URL_TEXT,
  DEFAULT_PROMO_CODE,
} from '@/services/marketplace-partnerships'
import PartnershipDetails from '@/components/marketplace/PartnershipDetails'
import Draggable from 'vuedraggable'
import store from 'store'
import { track } from '@/utils/analytics'

const GRID = 'GRID'
const TABLE = 'TABLE'

export default {
  components: {
    PartnershipDetails,
    Draggable,
  },
  props: {
    editable: { type: Boolean, default: false },
    draggable: { type: Boolean, default: false },
    partnerships: { type: Array, default: () => [] },
    viewmode: { type: String, default: GRID },
  },
  setup(props, { emit, root }) {
    const onEdit = ({ partnership }) => {
      emit('edit', { partnership })
    }

    const isDragEnabled = computed(() => props.editable && props.draggable)
    const dragOptions = computed(() => {
      return {
        group: 'discounts',
        disabled: !isDragEnabled.value,
        ghostClass: 'sortable-ghost',
        scrollSensitivity: 200,
        forceFallback: true,
      }
    })

    const visibleCount = ref(24)
    const showMore = () => {
      setTimeout(() => {
        visibleCount.value += 24
      }, 250)
    }
    const visiblePartnerships = computed({
      get: function () {
        if (isDragEnabled.value) {
          return store.state.marketplacePartnerships.entries
        }
        return props.partnerships.slice(0, visibleCount.value)
      },
      set: function (newValue) {
        return store.dispatch('marketplacePartnerships/setOrder', {
          partnerships: newValue,
        })
      },
    })

    const resetVisibleCount = () => {
      visibleCount.value = 24
    }
    watch('viewmode', resetVisibleCount)

    const revealedPartnershipId = ref(null)
    const revealPromocode = (id) => {
      revealedPartnershipId.value = id
    }
    const isPromocodeRevealed = (id) => {
      return revealedPartnershipId.value === id
    }

    const getUpdatedPartnershipDescription = (description) => {
      return description + '<div class="tail-fade"></div>'
    }

    const isDraft = (partnership) => {
      return partnership.is_published !== undefined && !partnership.is_published
    }

    const selectedPartnership = ref(null)
    const isPartnershipDetailsModalVisible = ref(false)
    const onPartnershipClick = ({ partnership }) => {
      selectedPartnership.value = partnership
      isPartnershipDetailsModalVisible.value = true

      track.click({
        category: 'Marketplace: Learn more button',
        label: partnership.name,
        partnership_id: partnership.id,
      })
    }

    const dialogWidth = computed(() => {
      if (['break-xs', 'break-small'].includes(root.$mq)) {
        return '100%'
      } else {
        return '600px'
      }
    })

    return {
      onEdit,
      GRID,
      TABLE,
      visiblePartnerships,
      isDraft,
      showMore,
      DEFAULT_PROMO_LINK_URL_TEXT,
      DEFAULT_PROMO_CODE,

      revealedPartnershipId,
      revealPromocode,
      isPromocodeRevealed,

      getUpdatedPartnershipDescription,
      onPartnershipClick,

      isPartnershipDetailsModalVisible,
      selectedPartnership,
      dialogWidth,

      dragOptions,
      isDragEnabled,
    }
  },
}
</script>

<style lang="scss">
@import '~assets/css/colors.scss';
@import '~assets/css/vars.scss';

.marketplace {
  padding-top: 10px;
  min-height: 300px;

  .partnership-item {
    height: 386px;
    margin-bottom: 10px;
    position: relative;

    .action-button {
      margin: 5px auto;
      max-width: 90%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .edit-button,
    .move-button {
      position: absolute;
      right: 10px;
      top: 10px;
      opacity: 0;
      color: $grey120;

      &:hover {
        color: $grey90;
        cursor: pointer;
      }
    }

    .move-button {
      right: 40px;
    }

    .image-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 120px;
      .card-img-top {
        max-height: 120px;
        max-width: 120px;
      }
    }
    .title {
      font-size: 16px;
      font-weight: 600;
      color: $grey90;
    }
    .tags {
      margin-bottom: 10px;
      height: 26px;
    }
    .description {
      font-size: 14px;
      font-weight: 400;
      color: $grey90;
      text-align: justify;
      height: 100px;
      overflow: hidden;
      span {
        display: inline-block;
      }
      position: relative;

      .tail-fade {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 40px;
        text-align: center;
        background-image: linear-gradient(to bottom, transparent, white);
      }
    }

    .promocode {
      font-size: 16px;
      font-weight: 500;
      letter-spacing: 2px;
    }

    &.is-draft {
      opacity: 0.9;
    }

    &:hover {
      // cursor: pointer;

      .edit-button,
      .move-button {
        opacity: 1;
      }
    }
  }
}

.marketplace {
  .partnership-row {
    .partnership-logo {
      max-height: 120px;
      max-width: 200px;
    }
  }

  .sortable-ghost {
    opacity: 0.3;
  }

  .draggable-enabled {
    .partnership-item {
      cursor: move;
    }
  }
}
.discount-drop-preview {
  background-color: rgba(150, 150, 200, 0.1);
  border: 1px dashed #abc;
  margin: 5px 45px 5px 5px;
}
</style>
